import React, { useState, useEffect, useRef } from 'react';

/**
 * DateInput — drop-in replacement for <input type="date"> that displays
 * DD/MM/YYYY regardless of browser locale.
 *
 * Public API matches the native input:
 *   - props: value (ISO 'YYYY-MM-DD' or ''), onChange(event), min, max, disabled, style
 *   - onChange is called with a synthetic event shape { target: { value: iso } }
 *
 * Internally the value is always ISO so existing save/min/max comparisons
 * keep working without any backend changes.
 */

const MONTHS = ['Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος','Μάιος','Ιούνιος','Ιούλιος','Αύγουστος','Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος'];
const WEEKDAYS_MON_FIRST = ['Δε','Τρ','Τε','Πε','Πα','Σα','Κυ'];

function isoToDisplay(iso) {
  if (!iso || typeof iso !== 'string') return '';
  const s = iso.slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function displayToIso(display) {
  if (!display) return null;
  const m = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const dd = parseInt(m[1], 10), mm = parseInt(m[2], 10), yyyy = parseInt(m[3], 10);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || yyyy < 1900 || yyyy > 2200) return null;
  const dt = new Date(Date.UTC(yyyy, mm - 1, dd));
  if (dt.getUTCFullYear() !== yyyy || dt.getUTCMonth() !== mm - 1 || dt.getUTCDate() !== dd) return null;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

function maskInput(raw) {
  const digits = (raw || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function isoToDate(iso) {
  if (!iso) return null;
  const m = iso.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
}

function dateToIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export default function DateInput({ value, onChange, min, max, disabled, style }) {
  const [text, setText] = useState(isoToDisplay(value));
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    const d = isoToDate(value) || new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const wrapperRef = useRef(null);

  useEffect(() => {
    setText(isoToDisplay(value));
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const emit = (iso) => {
    if (typeof onChange === 'function') onChange({ target: { value: iso || '' } });
  };

  const handleTextChange = (e) => {
    const formatted = maskInput(e.target.value);
    setText(formatted);
    if (formatted.length === 10) {
      const iso = displayToIso(formatted);
      if (iso) {
        if (min && iso < min) return;
        if (max && iso > max) return;
        emit(iso);
        const d = isoToDate(iso);
        if (d) setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1));
      }
    } else if (formatted === '') {
      emit('');
    }
  };

  const handleBlur = () => {
    if (text.length === 0) return;
    if (text.length !== 10 || !displayToIso(text)) {
      setText(isoToDisplay(value));
    }
  };

  const buildGrid = () => {
    const y = viewMonth.getFullYear();
    const m = viewMonth.getMonth();
    const firstDay = new Date(y, m, 1);
    const dow = (firstDay.getDay() + 6) % 7;
    const gridStart = new Date(y, m, 1 - dow);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      cells.push(d);
    }
    return cells;
  };

  const selectDate = (d) => {
    const iso = dateToIso(d);
    if (min && iso < min) return;
    if (max && iso > max) return;
    emit(iso);
    setText(isoToDisplay(iso));
    setOpen(false);
  };

  const goPrev = () => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  const goNext = () => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));

  const selectedIso = displayToIso(text) || (value && value.slice(0, 10)) || '';
  const tIso = todayIso();

  const containerStyle = { position: 'relative', width: '100%' };
  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    padding: (style && style.padding) || 10,
    paddingRight: 36,
    borderRadius: (style && style.borderRadius) || 8,
    border: (style && style.border) || '1px solid var(--border)',
    background: disabled ? '#f5f5f5' : '#fff',
    color: 'inherit',
    fontFamily: 'inherit',
    fontSize: (style && style.fontSize) || 14,
  };
  const iconBtnStyle = {
    position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
    width: 28, height: 28, background: 'transparent', border: 'none',
    cursor: disabled ? 'default' : 'pointer', fontSize: 16, padding: 0, color: '#888',
  };
  const popStyle = {
    position: 'absolute', top: '100%', left: 0, marginTop: 4,
    background: '#fff', border: '1px solid var(--border)', borderRadius: 8,
    boxShadow: '0 6px 24px rgba(0,0,0,0.12)', padding: 10, zIndex: 1000, width: 260,
  };

  return (
    <div ref={wrapperRef} style={containerStyle}>
      <input
        type="text"
        inputMode="numeric"
        value={text}
        onChange={handleTextChange}
        onBlur={handleBlur}
        placeholder="ΗΗ/ΜΜ/ΕΕΕΕ"
        disabled={disabled}
        style={inputStyle}
      />
      <button
        type="button"
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        disabled={disabled}
        aria-label="Άνοιγμα ημερολογίου"
        style={iconBtnStyle}
      >📅</button>
      {open && (
        <div style={popStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <button type="button" onClick={goPrev} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18, padding: '4px 10px', borderRadius: 4 }}>‹</button>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}</div>
            <button type="button" onClick={goNext} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18, padding: '4px 10px', borderRadius: 4 }}>›</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, fontSize: 11, color: '#888', textAlign: 'center', marginBottom: 4, fontWeight: 600 }}>
            {WEEKDAYS_MON_FIRST.map((w, i) => <div key={i}>{w}</div>)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {buildGrid().map((d, i) => {
              const iso = dateToIso(d);
              const inMonth = d.getMonth() === viewMonth.getMonth();
              const isSelected = selectedIso === iso;
              const isToday = iso === tIso;
              const outOfRange = (min && iso < min) || (max && iso > max);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => { if (!outOfRange) selectDate(d); }}
                  disabled={outOfRange}
                  style={{
                    padding: '6px 0',
                    fontSize: 12,
                    background: isSelected ? '#C0185A' : 'transparent',
                    color: isSelected ? '#fff' : (outOfRange ? '#ccc' : (inMonth ? 'inherit' : '#bbb')),
                    border: isToday && !isSelected ? '1px solid #C0185A' : '1px solid transparent',
                    borderRadius: 4,
                    cursor: outOfRange ? 'not-allowed' : 'pointer',
                    fontWeight: (isSelected || isToday) ? 700 : 400,
                  }}
                >{d.getDate()}</button>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, gap: 6 }}>
            <button type="button" onClick={() => { emit(''); setText(''); setOpen(false); }} style={{ flex: 1, padding: 6, fontSize: 12, background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }}>Καθαρισμός</button>
            <button type="button" onClick={() => { const t = todayIso(); if ((min && t < min) || (max && t > max)) return; emit(t); setText(isoToDisplay(t)); setOpen(false); }} style={{ flex: 1, padding: 6, fontSize: 12, background: '#C0185A', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Σήμερα</button>
          </div>
        </div>
      )}
    </div>
  );
}
